import gql from 'graphql-tag';
import _ from 'lodash';
import {useEffect, useMemo, useRef, useState} from 'react';

import * as Generated from '../../generated/graphql';
import {
  EMPTY_HISTORY_KEY_INFO,
  type RunHistoryKeyInfo,
  type RunHistoryKeyInfoEntry,
  type RunHistoryKeyInfoKeys,
  type RunHistoryKeyType,
} from '../../types/run';
import {useCachedData} from '../../util/cache';
import {usePersistentRumViewContext} from '../../util/datadog/datadogRum';
import {useHistoryKeyFetchPerformanceMarks} from '../../util/datadog/longTasks_EXPERIMENTAL_DO_NOT_USE/workspaceSyncPerformanceMarks';
import {usePerformanceStatLogger} from '../../util/datadog/usePerformanceStatLogger';
import {projectScaleBucket} from '../../util/datadog/workspace/projectScale';
import {propagateErrorsContext} from '../../util/errors';
import * as Filter from '../../util/filters';
import type * as FilterTypes from '../../util/filterTypes';
import * as QueryTS from '../../util/queryts';
import type * as RunTypes from '../../util/runTypes';
import {useLatchedInvalidRunFilter} from '../../util/useInvalidRunFilter';
import {useApolloClient} from '../hooks';
import {NetworkStatus} from './apolloWithAbort';
import {type HistoryKeyInfoQueryVars} from './historyKeysQueryTypes';
import {useQuery} from './query';

/** Default run cap for the main `runs` branch of `HistoryKeysWithHash`. */
export const HISTORY_KEYS_MAIN_RUN_LIMIT = 100;

/**
 * Keeps total backend run budget at {@link HISTORY_KEYS_MAIN_RUN_LIMIT}
 * by subtracting pinned runs count from the limit
 */
export function getHistoryKeysMainQueryLimit(
  includePinnedRuns: boolean,
  pinnedFilters?: FilterTypes.Filter<RunTypes.Key>
): number {
  const pinnedRunCount =
    pinnedFilters?.op === 'OR' ? pinnedFilters.filters.length : 0;
  if (!includePinnedRuns || pinnedRunCount <= 0) {
    return HISTORY_KEYS_MAIN_RUN_LIMIT;
  }
  return Math.max(HISTORY_KEYS_MAIN_RUN_LIMIT - pinnedRunCount, 0);
}

const HISTORY_KEYS_RUNS_FRAGMENT = gql`
  fragment HistoryKeysRunsFields on RunConnection {
    historyKeys(format: PLAINTEXT, includeHash: true)
  }
`;

export const HISTORY_KEYS_QUERY = gql`
  query HistoryKeys(
    $projectName: String!
    $entityName: String!
    $filters: JSONString
    $limit: Int = 100
    $order: String
  ) {
    project(name: $projectName, entityName: $entityName) {
      id
      runs(filters: $filters, first: $limit, order: $order) {
        historyKeys(format: PLAINTEXT)
      }
    }
  }
`;

export const HISTORY_KEYS_WITH_HASH_QUERY = gql`
  query HistoryKeysWithHash(
    $projectName: String!
    $entityName: String!
    $filters: JSONString
    $limit: Int = 100
    $order: String
    $pinnedFilters: JSONString
    $includePinnedRuns: Boolean = false
  ) {
    project(name: $projectName, entityName: $entityName) {
      id
      runs(filters: $filters, first: $limit, order: $order) {
        ...HistoryKeysRunsFields
      }
      pinnedRuns: runs(filters: $pinnedFilters)
        @include(if: $includePinnedRuns) {
        ...HistoryKeysRunsFields
      }
    }
  }
  ${HISTORY_KEYS_RUNS_FRAGMENT}
`;

export const HISTORY_KEYS_QUERY_BY_PROJECT_ID = gql`
  query HistoryKeysByProjectId(
    $internalId: ID!
    $filters: JSONString
    $limit: Int = 100
    $order: String
  ) {
    project(internalId: $internalId) {
      id
      runs(filters: $filters, first: $limit, order: $order) {
        historyKeys(format: BITMAP)
      }
    }
  }
`;

interface RunHistoryKeyInfoServerResponse {
  keys: RunHistoryKeyInfoKeys;
  lastStep?: number;
  hash?: string;
}

interface RunsConnectionData {
  historyKeys: RunHistoryKeyInfoServerResponse;
}

interface Data {
  project: {
    runs: RunsConnectionData;
    pinnedRuns?: RunsConnectionData;
  };
}

function mergeKeyTypeCounts(
  main: RunHistoryKeyInfoEntry['typeCounts'],
  pinned: RunHistoryKeyInfoEntry['typeCounts']
): RunHistoryKeyInfoEntry['typeCounts'] {
  if (main == null) {
    return pinned;
  }
  if (pinned == null) {
    return main;
  }

  const countsByType = new Map<RunHistoryKeyType, number>();
  for (const {type, count} of [...main, ...pinned]) {
    countsByType.set(type, (countsByType.get(type) ?? 0) + count);
  }

  return Array.from(countsByType.entries()).map(([type, count]) => ({
    type,
    count,
  }));
}

function mergeKeyInfoEntry(
  main: RunHistoryKeyInfoEntry,
  pinned: RunHistoryKeyInfoEntry
): RunHistoryKeyInfoEntry {
  const typeCounts = mergeKeyTypeCounts(main.typeCounts, pinned.typeCounts);
  const monotonic = main.monotonic === true && pinned.monotonic === true;

  const merged: RunHistoryKeyInfoEntry = {};
  if (typeCounts != null && typeCounts.length > 0) {
    merged.typeCounts = typeCounts;
  }
  if (monotonic) {
    merged.monotonic = true;
  }
  return merged;
}

function mergeHistoryKeyInfoKeys(
  main: RunHistoryKeyInfoKeys,
  pinned: RunHistoryKeyInfoKeys
): RunHistoryKeyInfoKeys {
  // Fast path: with no pinned runs (the common case) there is nothing to merge, so return
  // `main` directly instead of cloning a Record that can hold 500k+ entries — the clone
  // would just produce a byte-identical object. Safe because the merged keys are read-only
  // downstream (keyTypes / getBaseXYChoices / sortedKeys only read them) and Apollo freezes
  // query results.
  const pinnedKeys = Object.keys(pinned);
  if (pinnedKeys.length === 0) {
    return main;
  }
  const merged = {...main};
  for (const key of pinnedKeys) {
    const pinnedEntry = pinned[key];
    if (key in merged) {
      merged[key] = mergeKeyInfoEntry(merged[key], pinnedEntry);
    } else {
      merged[key] = pinnedEntry;
    }
  }
  return merged;
}

/**
 * Merges RunHistoryKeyInfo results for pinned runs and main runs into one dataset.
 */
export function mergeHistoryKeyInfoServerResponses(
  main: RunHistoryKeyInfoServerResponse | null | undefined,
  pinned: RunHistoryKeyInfoServerResponse | null | undefined
): RunHistoryKeyInfoServerResponse | undefined {
  if (main == null && pinned == null) {
    return undefined;
  }
  return {
    keys: mergeHistoryKeyInfoKeys(main?.keys ?? {}, pinned?.keys ?? {}),
    // Use -1 as default to match backend's "no history yet" signal
    lastStep: Math.max(main?.lastStep ?? -1, pinned?.lastStep ?? -1),
    // Hash is a composite of the two hashes for downstream memo purposes
    hash:
      main?.hash != null || pinned?.hash != null
        ? `${main?.hash ?? ''}:${pinned?.hash ?? ''}`
        : undefined,
  };
}

// For really big result sets, we set a much longer interval
const LONG_POLL_KEYS_THRESHOLD = 1000;

export interface VizMap {
  [key: string]: any;
}

export function mergeViz(
  viz: Array<{viz?: any; visualize?: any} | null | undefined>
): VizMap {
  const res: VizMap = {};
  viz.forEach(parsed => {
    if (parsed == null) {
      return;
    }
    if (parsed.viz == null && parsed.visualize == null) {
      return;
    }
    const v = parsed.viz;
    if (v != null) {
      Object.keys(v).forEach(k => {
        if (res[k] == null) {
          res[k] = v[k];
        }
      });
    }

    const visualize = parsed.visualize;
    if (visualize != null) {
      Object.keys(visualize).forEach(k => {
        if (res[k] == null) {
          res[k] = visualize[k];
        }
      });
    }
  });
  return res;
}

// WB-9640: A bug in the client is persisting inefficient user queries for custom charts
// Basically, summaryTable field is over-fetching because `tableColumns` arg is missing
// This function tries its best to find these malformed configs and adds the missing arg.
export function patchVizMap(vizMap: VizMap): VizMap {
  const result = _.mapValues(vizMap, viz => {
    (function fixViz() {
      if (
        viz.panel_config?.panelDefId == null ||
        ![
          'wandb/bar/v0',
          'wandb/confusion_matrix/v1',
          'wandb/histogram/v0',
          'wandb/lineseries/v0',
          'wandb/line/v0',
          'wandb/area-under-curve/v0',
          'wandb/scatter/v0',
        ].includes(viz.panel_config?.panelDefId)
      ) {
        return;
      }

      // Collect used fields
      const fieldSettings = viz.panel_config?.fieldSettings;
      if (fieldSettings == null) {
        // Unexpected viz shape
        return;
      }
      const usedFields = Object.values(fieldSettings).filter(f => f != null);

      const queryFields = viz.panel_config?.userQuery?.queryFields[0]?.fields;
      if (queryFields == null) {
        // Unexpected viz shape
        return;
      }

      // Check above obviates null-chaining
      viz.panel_config.userQuery.queryFields[0].fields = queryFields.map(
        (field: any) => {
          (function fixQueryField() {
            if (field.name !== 'summaryTable') {
              // Only fix the summaryTable field's args
              return;
            }

            const args: Array<{name: string; value: any}> = field.args;
            if (args.find(arg => arg.name === 'tableColumns') != null) {
              // Already has a tableColumns arg
              return;
            }

            const tableKeyArg = args.find(arg => arg.name === 'tableKey');
            if (
              tableKeyArg == null ||
              !(tableKeyArg.value as string).endsWith('_table')
            ) {
              // This didn't have a tableKey arg, or it doesn't have the expected format!
              return;
            }

            field.args.push({name: 'tableColumns', value: usedFields});
          })();

          return field;
        }
      );
    })();

    return viz;
  });

  return result;
}

type HistoryKeyLoggingInfo = {
  keys?: Record<string, unknown>;
  lastStep?: number;
};
function useHistoryKeyLogging(
  historyKeyInfo: HistoryKeyLoggingInfo | undefined,
  // keyCount is passed in (not derived here) so we don't re-materialize Object.keys on a
  // potentially 500k+ entry Record; the caller already has the count cheaply.
  keyCount: number
) {
  const stepCount = historyKeyInfo?.lastStep ?? 0;
  usePerformanceStatLogger('workspace.keys_count', keyCount, v => v >= 100);
  usePerformanceStatLogger(
    'workspace.steps_count',
    stepCount,
    v => v >= 1_000_000
  );
  usePersistentRumViewContext('workspace.keys_count', keyCount, keyCount > 0);
  usePersistentRumViewContext(
    'workspace.steps_count',
    stepCount,
    stepCount > 0
  );
  usePersistentRumViewContext(
    'workspace.keys_count_bucket',
    projectScaleBucket(keyCount),
    keyCount > 0
  );
  usePersistentRumViewContext(
    'workspace.steps_count_bucket',
    projectScaleBucket(stepCount),
    stepCount > 0
  );
}

// For tests only
export {patchVizMap as __patchVizMap};

export type UseHistoryKeysQueryResult =
  | {loading: true; error: null; refreshing: false}
  | {loading: false; error: Error; refreshing: false}
  | ({
      loading: false;
      error: null;
      refreshing: boolean;
    } & HistoryKeysResult);

type HistoryKeysResult = {
  historyKeyInfo: RunHistoryKeyInfo;
};

const EMPTY_HISTORY_KEYS_RESULT: HistoryKeysResult = {
  historyKeyInfo: EMPTY_HISTORY_KEY_INFO,
};

// Apollo keeps previous data available while variables change. Treat that as a
// fresh load so stale history keys are not rendered or cached under the new key.
export const isWaitingForFreshHistoryKeys = (
  initialLoading: boolean,
  networkStatus?: NetworkStatus,
  loading?: boolean,
  hasQueryData?: boolean
) =>
  initialLoading ||
  networkStatus === NetworkStatus.setVariables ||
  (loading === true && hasQueryData === false);

// This query is polled, respecting the user polling settings and the page poll
// interval (stored in redux). If the query is expensive (determined by a simple
// threshold on the result size), we raise the poll interval to a much larger
// value, to reduce network overhead for users.
export function useHistoryKeysQuery(
  queryVars: HistoryKeyInfoQueryVars,
  skip?: boolean
): UseHistoryKeysQueryResult {
  const {
    entityName,
    projectName,
    filters,
    sort,
    pinnedFilters,
    includePinnedRuns = false,
  } = queryVars;
  const [pollMultiplier, setPollMultiplier] = useState<number>(1);

  const filtersStr = JSON.stringify(Filter.toMongo(filters));
  const orderStr = QueryTS.sortToOrderString(sort);
  const pinnedFiltersStr = JSON.stringify(
    Filter.toMongo(pinnedFilters ?? Filter.Or([]))
  );
  const limit = getHistoryKeysMainQueryLimit(includePinnedRuns, pinnedFilters);
  const variables = useMemo(
    () => ({
      entityName,
      projectName,
      filters: filtersStr,
      order: orderStr,
      limit,
      pinnedFilters: pinnedFiltersStr,
      includePinnedRuns,
    }),
    [
      entityName,
      projectName,
      filtersStr,
      orderStr,
      limit,
      pinnedFiltersStr,
      includePinnedRuns,
    ]
  );
  const query = useQuery<Data, Generated.HistoryKeysWithHashQueryVariables>(
    Generated.HistoryKeysWithHashDocument,
    {
      variables,
      enablePolling: true,
      pollMultiplier,
      context: propagateErrorsContext(),
      notifyOnNetworkStatusChange: true,
      skip,
    }
  );

  const isInvalidRunFilter = useLatchedInvalidRunFilter(
    query.error,
    filtersStr
  );

  const waitingForFreshHistoryKeys = isWaitingForFreshHistoryKeys(
    query.initialLoading,
    query.networkStatus,
    query.loading,
    query.data?.project != null
  );
  const refreshing = waitingForFreshHistoryKeys && !query.initialLoading;
  // A rejected filter leaves data.project present but without the runs field for
  // those variables, so project?.runs.historyKeys throws into the workspace error
  // boundary. Drop the project rather than deriving off it.
  const project =
    waitingForFreshHistoryKeys || isInvalidRunFilter
      ? null
      : query.data?.project;
  const mainHistoryKeyInfo = project?.runs.historyKeys;
  const pinnedHistoryKeyInfo = project?.pinnedRuns?.historyKeys;
  const mergedHistoryKeyInfo = useMemo(
    () =>
      mergeHistoryKeyInfoServerResponses(
        mainHistoryKeyInfo,
        pinnedHistoryKeyInfo
      ),
    [mainHistoryKeyInfo, pinnedHistoryKeyInfo]
  );

  const resultFromServer: HistoryKeysResult | null = useMemo(() => {
    if (project == null || mergedHistoryKeyInfo == null) {
      return null;
    }

    return {
      historyKeyInfo: {
        ...mergedHistoryKeyInfo,
        sortedKeys: Object.keys(mergedHistoryKeyInfo.keys).sort(),
        hash: mergedHistoryKeyInfo.hash,
      },
    };
  }, [project, mergedHistoryKeyInfo]);

  useHistoryKeyFetchPerformanceMarks({
    fetchKey: variables,
    isFetchingUpdate: query.networkStatus === NetworkStatus.setVariables,
    hasFreshResult:
      query.networkStatus !== NetworkStatus.setVariables &&
      query.error == null &&
      !isInvalidRunFilter &&
      resultFromServer != null,
  });

  // Reuse the sortedKeys array the result memo already materialized rather than running a
  // second Object.keys() over the (up to 500k+ entry) Record just to count. resultFromServer
  // is null exactly when mergedHistoryKeyInfo is null (both derive from `project`), so this
  // matches the previous count's null behavior.
  const keyCount = resultFromServer?.historyKeyInfo.sortedKeys.length ?? 0;

  const prevResult = useRef<HistoryKeysResult | null>(null);
  const resultFromServerOrCache = useCachedData({
    namespace: `useHistoryKeysQuery`,
    key: variables,
    dataFromServer: resultFromServer,
    waitingForDataFromServer: waitingForFreshHistoryKeys,
  });

  const result = useMemo(() => {
    if (resultFromServerOrCache == null) {
      return null;
    }

    const hash = resultFromServerOrCache.historyKeyInfo.hash;
    const prevHash = prevResult.current?.historyKeyInfo.hash;

    // If the hash is the same as the previous hash, we can use the previous result
    // This prevents re-renders and scripting if the history keys haven't changed
    if (
      hash != null &&
      prevHash != null &&
      hash === prevHash &&
      prevResult.current != null
    ) {
      return prevResult.current;
    }

    // Otherwise, return the new result (which will trigger scripting)
    return resultFromServerOrCache;
  }, [resultFromServerOrCache]);

  useEffect(() => {
    if (result != null) {
      prevResult.current = result;
    }
  }, [result]);

  useEffect(() => {
    if (!waitingForFreshHistoryKeys && mergedHistoryKeyInfo != null) {
      setPollMultiplier(keyCount > LONG_POLL_KEYS_THRESHOLD ? 10 : 1);
    }
  }, [waitingForFreshHistoryKeys, mergedHistoryKeyInfo, keyCount]);

  useHistoryKeyLogging(mergedHistoryKeyInfo, keyCount);

  return useMemo(() => {
    if (isInvalidRunFilter) {
      return {
        loading: false,
        error: null,
        refreshing: false,
        ...EMPTY_HISTORY_KEYS_RESULT,
      };
    }
    if (query.initialLoading && result == null) {
      return {loading: true, error: null, refreshing: false};
    }
    if (!waitingForFreshHistoryKeys && project == null) {
      return {
        loading: false,
        error:
          query.error ?? new Error('Failed to load workspace history keys'),
        refreshing: false,
      };
    }
    if (result == null) {
      if (refreshing) {
        return {
          loading: false,
          error: null,
          refreshing: true,
          ...(prevResult.current ?? EMPTY_HISTORY_KEYS_RESULT),
        };
      }
      throw new Error(`invalid state`);
    }
    return {
      loading: false,
      error: null,
      refreshing,
      ...result,
    };
  }, [
    isInvalidRunFilter,
    query.initialLoading,
    query.error,
    waitingForFreshHistoryKeys,
    project,
    refreshing,
    result,
  ]);
}

export function useMultiRunsetHistoryKeysQuery(
  queryVars: HistoryKeyInfoQueryVars[]
): UseHistoryKeysQueryResult {
  const [pollMultiplier, setPollMultiplier] = useState<number>(1);

  const [pollCount, setPollCount] = useState(0);
  const [resultFromServer, setResultFromServer] =
    useState<HistoryKeysResult | null>(null);
  const [err, setErr] = useState<Error | null>(null);

  const client = useApolloClient();
  useEffect(() => {
    const fetchHistoryKeys = async () => {
      const projectAccessPromises = queryVars.map(async ps => {
        const variables = {
          entityName: ps.entityName,
          projectName: ps.projectName,
          filters: JSON.stringify(Filter.toMongo(ps.filters)),
          order: QueryTS.sortToOrderString(ps.sort),
        };
        try {
          const {data} = await client.query<
            Generated.HistoryKeysQueryResult['data']
          >({
            query: Generated.HistoryKeysDocument,
            variables,
          });
          return data;
        } catch (error) {
          console.error(error);
          setErr(error as Error);
          return null;
        }
      });

      const results = await Promise.all(projectAccessPromises);
      const validResults = results.filter(result => result != null);
      const allKeys = Object.assign(
        {} as RunHistoryKeyInfoKeys,
        ...validResults.map(r => r?.project?.runs?.historyKeys?.keys)
      );
      const mergedResult: HistoryKeysResult = {
        historyKeyInfo: {
          keys: allKeys,
          sortedKeys: Object.keys(allKeys).sort(),
        },
      };
      if (mergedResult == null || Object.keys(mergedResult).length === 0) {
        return;
      }
      setResultFromServer(mergedResult);
    };

    fetchHistoryKeys();
  }, [queryVars, client, pollCount]); // N.B pollCount is used to force a re-fetch

  useEffect(() => {
    setErr(null);
  }, [queryVars]);

  const isInvalidRunFilter = useLatchedInvalidRunFilter(
    err,
    JSON.stringify(queryVars)
  );

  // polling hack, force polling by setting pollCount
  useEffect(() => {
    const interval = setInterval(() => {
      setPollCount(pollCount + 1);
    }, 5000 * pollMultiplier);
    return () => clearInterval(interval);
  }, [pollMultiplier, pollCount]);

  const initialLoading = resultFromServer == null && err == null;

  const result = useCachedData({
    namespace: `useHistoryKeysQueryIncrementally`,
    key: queryVars,
    dataFromServer: resultFromServer,
    waitingForDataFromServer: initialLoading,
  });

  const historyKeyInfo = result?.historyKeyInfo;
  // sortedKeys is already materialized when the result is built, so reading its length is
  // free — no need to re-run Object.keys() on the full Record here.
  const keyCount = historyKeyInfo?.sortedKeys.length ?? 0;
  useEffect(() => {
    if (!initialLoading && historyKeyInfo != null) {
      setPollMultiplier(keyCount > LONG_POLL_KEYS_THRESHOLD ? 10 : 1);
    }
  }, [initialLoading, historyKeyInfo, keyCount]);

  useHistoryKeyLogging(historyKeyInfo, keyCount);

  return useMemo(() => {
    if (isInvalidRunFilter) {
      return {
        loading: false,
        error: null,
        refreshing: false,
        ...EMPTY_HISTORY_KEYS_RESULT,
      };
    }
    if (err != null) {
      return {loading: false, error: err, refreshing: false};
    }
    if (initialLoading) {
      return {
        loading: true,
        error: null,
        refreshing: false,
      };
    }
    if (result == null) {
      throw new Error(`invalid state`);
    }
    return {
      loading: false,
      error: null,
      refreshing: false,
      ...result,
    };
  }, [initialLoading, err, result, isInvalidRunFilter]);
}
